/**
 * Hook pour recuperer la liste des utilisateurs (admin)
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AdminUser {
  id: string;
  email: string | null;
  prenom: string | null;
  nom: string | null;
  created_at: string;
  role: string | null;
  entreprise_nom: string | null;
  entreprise_id: string | null;
  plan: string | null;
}

interface UseAdminUsersOptions {
  page?: number;
  pageSize?: number;
  search?: string;
}

export function useAdminUsers(options: UseAdminUsersOptions = {}) {
  const { page = 1, pageSize = 20, search = '' } = options;

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, search]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      // Construire la requete de base
      let query = supabase
        .from('profiles')
        .select(`
          id,
          email,
          prenom,
          nom,
          created_at
        `, { count: 'exact' });

      // Filtres de recherche
      if (search) {
        query = query.or(`email.ilike.%${search}%,prenom.ilike.%${search}%,nom.ilike.%${search}%`);
      }

      // Pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      query = query.range(from, to).order('created_at', { ascending: false });

      const { data: profilesData, count, error: queryError } = await query;

      if (queryError) throw queryError;

      // Pour chaque profil, recuperer les roles, entreprises et subscriptions
      const transformedUsers: AdminUser[] = await Promise.all(
        (profilesData || []).map(async (profile) => {
          // Recuperer les roles
          const { data: rolesData } = await supabase
            .from('user_roles')
            .select('role')
            .eq('user_id', profile.id);

          // Recuperer l'entreprise
          const { data: entrepriseData } = await supabase
            .from('entreprises')
            .select('id, nom')
            .eq('proprietaire_user_id', profile.id)
            .single();

          // Recuperer la subscription via l'entreprise
          let plan: string | null = 'free';
          if (entrepriseData?.id) {
            const { data: subData } = await supabase
              .from('subscriptions')
              .select('plan')
              .eq('entreprise_id', entrepriseData.id)
              .single();
            plan = subData?.plan || 'free';
          }

          return {
            id: profile.id,
            email: profile.email,
            prenom: profile.prenom,
            nom: profile.nom,
            created_at: profile.created_at,
            role: rolesData?.[0]?.role || null,
            entreprise_nom: entrepriseData?.nom || null,
            entreprise_id: entrepriseData?.id || null,
            plan,
          };
        })
      );

      setUsers(transformedUsers);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('[useAdminUsers] Erreur:', err);
      setError(err instanceof Error ? err : new Error('Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  return {
    users,
    totalCount,
    totalPages: Math.ceil(totalCount / pageSize),
    loading,
    error,
    refetch: fetchUsers,
  };
}
